'use client';

import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { TextReveal } from '@/components/shared/TextReveal';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import type { ValueStep } from '@/lib/content-schema';

interface Props {
  steps: ValueStep[];
}

export function ValueProposition({ steps }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="como-funciona" className="py-24 md:py-32 bg-zibarit-bone">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="initial"
          animate={isInView ? 'animate' : 'initial'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6"
        >
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              variants={fadeInUp}
              className="relative"
            >
              {/* Connector arrow (hidden on last item and mobile) */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 -right-3 text-zibarit-marron-secundario text-2xl">
                  &rarr;
                </div>
              )}

              <div className="text-center lg:text-left">
                <span className="text-title text-6xl md:text-7xl text-zibarit-marron-secundario/20 font-bold">
                  {step.number}
                </span>
                <TextReveal
                  text={step.title}
                  as="h3"
                  className="text-title text-2xl md:text-3xl text-zibarit-negro -mt-4"
                  delay={0.2 + index * 0.1}
                />
                <p className="text-body text-sm text-zibarit-marron-oscuro mt-4">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
